import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/apiService';
import ConfirmationDialog from '../components/ConfirmationDialog';
import ROUTING_ACTION_TYPES from '../utils/routingActionTypes';
import './SetupAgentPage.css';
import './ViewAgentPage.css';
import './AttributeRegistryPage.css';
import {
  getAvailableEnvironments,
  getSelectedEnvironment,
  saveSelectedEnvironment,
  getApiEndpoints,
  ENVIRONMENT_LABELS,
  getEnvironmentColor
} from '../utils/apiConfig';

const ACTION_TYPES = Object.values(ROUTING_ACTION_TYPES);
const TIMEOUT_OPTIONS = Array.from({ length: 61 }, (_, index) => index * 5);
const PRIORITY_OPTIONS = Array.from({ length: 100 }, (_, index) => index + 1);
const US_TIME_ZONES = [
  { value: 'America/New_York', label: 'Eastern Time' },
  { value: 'America/Chicago', label: 'Central Time' },
  { value: 'America/Denver', label: 'Mountain Time' },
  { value: 'America/Phoenix', label: 'Mountain Time (Arizona)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time' },
  { value: 'America/Anchorage', label: 'Alaska Time' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time' }
];

const createCondition = () => ({
  attributeId: '',
  operator: '',
  value: ''
});

const isNumericAction = (actionType) => (
  actionType === ROUTING_ACTION_TYPES.CHANGE_TIMEOUT ||
  actionType === ROUTING_ACTION_TYPES.SET_PRIORITY
);

const toDateTimeLocal = (value) => value ? value.replace(/Z$/, '') : '';

const getRestrictionStatusLabel = (restriction) => {
  const status = restriction.status ?? restriction.active;
  if (typeof status === 'boolean') return status ? 'Active' : 'Inactive';
  return String(status || '').toLowerCase() === 'inactive' ? 'Inactive' : 'Active';
};

const formatCriteria = (contactCriteria) => {
  if (!contactCriteria) return '';
  const { logic, conditions } = contactCriteria;
  if (!conditions || conditions.length === 0) return '';
  return conditions.map((condition) => `${condition.attributeId} ${condition.operator} ${condition.value}`).join(` ${logic} `);
};

const formatActions = (actions) => {
  if (!actions || actions.length === 0) return '';
  return actions.map((action) => {
    const parts = [];
    if (action.type) parts.push(action.type);
    if (action.routingGroupIds && action.routingGroupIds.length > 0) parts.push(`Routing Groups: ${action.routingGroupIds.join(', ')}`);
    if (action.timeoutSeconds != null) parts.push(`Timeout: ${action.timeoutSeconds}s`);
    if (action.priority != null) parts.push(`Priority: ${action.priority}`);
    return parts.join(' | ');
  }).join(' ; ');
};

const getSortValue = (restriction, key) => {
  if (key === 'contactCriteria') {
    return formatCriteria(restriction.contactCriteria).toLowerCase();
  }
  if (key === 'actions') {
    return formatActions(restriction.actions).toLowerCase();
  }
  if (key === 'status') {
    return getRestrictionStatusLabel(restriction).toLowerCase();
  }
  return String(restriction[key] ?? '').toLowerCase();
};

const RoutingRestrictionsPage = () => {
  const [restrictions, setRestrictions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [instances, setInstances] = useState([]);
  const [form, setForm] = useState({
    instanceId: '',
    restrictionId: '',
    restrictionName: '',
    description: '',
    status: 'active',
    contactCriteria: {
      logic: 'AND',
      conditions: [createCondition()]
    },
    actions: [
      {
        type: ACTION_TYPES[0],
        routingGroupIds: [],
        timeoutSeconds: '',
        priority: ''
      }
    ],
    effectiveTime: {
      startTime: '',
      endTime: '',
      timezone: 'America/Chicago'
    }
  });
    const [loadingInstances, setLoadingInstances] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, routingGroup: null });


  const availableEnvironments = getAvailableEnvironments();
  const [selectedEnvironment, setSelectedEnvironment] = useState(getSelectedEnvironment());

   const endpoints = getApiEndpoints(selectedEnvironment);

  // Handle environment change
  const handleEnvironmentChange = (e) => {
    const newEnvironment = e.target.value;
    setSelectedEnvironment(newEnvironment);
    saveSelectedEnvironment(newEnvironment);
    setResult(null);
    setError(null);
  };

  const getUserRole = () => (
    JSON.parse(localStorage.getItem('user') || 'null')?.role || ''
  );

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const loadInstanceData = async (instanceId) => {
    setLoadingData(true);
    setRestrictions([]);
    setGroups([]);
    setAttributes([]);

    try {
      const responses = await Promise.all([
        apiService.post(endpoints.routingRestrictions, {
          instanceId: instanceId,
          user_role: getUserRole()
        }),
        apiService.post(endpoints.routingGroups, {
          instanceId: instanceId,
          user_role: getUserRole()
        }),
        apiService.post(endpoints.attributesView, {
          instance_id: instanceId,
          user_role: getUserRole()
        })
      ]);

      if (responses.some((response) => response.data.status !== 'ok')) {
        throw new Error('Failed to load restriction data');
      }

      setRestrictions(responses[0].data.result?.routingRestrictions || []);
      setGroups(responses[1].data.result?.routingGroups || []);
      setAttributes(responses[2].data.result?.attributes || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load restriction data');
    } finally {
      setLoadingData(false);
    }
  };

  const handleInstanceChange = (e) => {
    const instanceId = e.target.value;

    updateForm('instanceId', instanceId);
    setError(null);
    setResult(null);

    if (instanceId) {
      loadInstanceData(instanceId);
    } else {
      setRestrictions([]);
      setGroups([]);
      setAttributes([]);
    }
  };

  const updateCondition = (index, field, value) => {
    updateForm('contactCriteria', {
      ...form.contactCriteria,
      conditions: form.contactCriteria.conditions.map((condition, conditionIndex) => (
        conditionIndex === index
          ? {
              ...condition,
              [field]: value,
              ...(field === 'attributeId'
                ? {
                    operator: Object.entries(attributes.find((attribute) => attribute.attributeId === value)?.allowedOperators || {})
                      .some(([, allowed]) => allowed) ? '' : 'EQUALS',
                    value: ''
                  }
                : {})
            }
          : condition
      ))
    });
  };

  const addCondition = () => {
    updateForm('contactCriteria', {
      ...form.contactCriteria,
      conditions: [...form.contactCriteria.conditions, createCondition()]
    });
  };

  const removeCondition = (index) => {
    updateForm('contactCriteria', {
      ...form.contactCriteria,
      conditions: form.contactCriteria.conditions.filter(
        (_, conditionIndex) => conditionIndex !== index
      )
    });
  };

  const updateAction = (index, field, value) => {
    updateForm('actions', form.actions.map((action, actionIndex) => (
      actionIndex === index ? { ...action, [field]: value } : action
    )));
  };

  const addAction = () => {
    updateForm('actions', [
      ...form.actions,
      {
        type: ACTION_TYPES[0],
        routingGroupIds: [],
        timeoutSeconds: '',
        priority: ''
      }
    ]);
  };

  const removeAction = (index) => {
    updateForm('actions', form.actions.filter((_, actionIndex) => actionIndex !== index));
  };

  const updateRoutingGroups = (index, e) => {
    updateAction(
      index,
      'routingGroupIds',
      Array.from(e.target.selectedOptions, (option) => option.value)
    );
  };

  const handleActionTypeChange = (index, actionType) => {
    updateForm('actions', form.actions.map((action, actionIndex) => (
      actionIndex === index
        ? { ...action, type: actionType, routingGroupIds: [], timeoutSeconds: '', priority: '' }
        : action
    )));
  };

  const getPayload = () => ({
    ...form,
    actions: form.actions.map((action) => ({
      type: action.type,
      ...(isNumericAction(action.type)
        ? action.type === ROUTING_ACTION_TYPES.CHANGE_TIMEOUT
          ? { timeoutSeconds: action.timeoutSeconds === '' ? null : Number(action.timeoutSeconds) }
          : { priority: action.priority === '' ? null : Number(action.priority) }
        : { routingGroupIds: action.routingGroupIds })
    })),
    effectiveTime: {
      ...form.effectiveTime,
      startTime: form.effectiveTime.startTime || null,
      endTime: form.effectiveTime.endTime || null
    },
    user_role: getUserRole()
  });

  const validateRestriction = () => {
    const requiredFields = ['restrictionId', 'restrictionName', 'instanceId'];
    const hasRequiredFields = requiredFields.every((field) => form[field]);
    const validConditions = form.contactCriteria.conditions.every((condition) => (
      condition.attributeId && condition.operator && condition.value
    ));
    const validActions = form.actions.length > 0 && form.actions.every((action) => {
      if (action.type === ROUTING_ACTION_TYPES.CHANGE_TIMEOUT) {
        return action.timeoutSeconds !== '';
      }
      if (action.type === ROUTING_ACTION_TYPES.SET_PRIORITY) {
        return action.priority !== '';
      }
      return action.type && action.routingGroupIds.length > 0;
    });

    setResult({
      message: hasRequiredFields && validConditions && validActions
        ? 'Test passed: restriction configuration is valid.'
        : 'Test failed: complete all required restriction fields.'
    });
  };

  const loadRestriction = (restriction) => {
    setForm((prev) => ({
      instanceId: prev.instanceId,
      restrictionId: restriction.restrictionId || '',
      restrictionName: restriction.restrictionName || '',
      description: restriction.description || '',
      status: restriction.status || 'active',
      contactCriteria: {
        logic: restriction.contactCriteria?.logic || 'AND',
        conditions: (restriction.contactCriteria?.conditions || [createCondition()])
          .map((condition) => ({ ...condition }))
      },
      actions: (restriction.actions || []).map((action) => ({
        type: action.type || ACTION_TYPES[0],
        routingGroupIds: [...(action.routingGroupIds || [])],
        timeoutSeconds: action.timeoutSeconds ?? '',
        priority: action.priority ?? ''
      })),
      effectiveTime: {
        startTime: toDateTimeLocal(restriction.effectiveTime?.startTime),
        endTime: toDateTimeLocal(restriction.effectiveTime?.endTime),
        timezone: restriction.effectiveTime?.timezone || 'America/Chicago'
      }
    }));
    setError(null);
    setResult({ message: `Loaded routing restriction '${restriction.restrictionId}'.` });
  };

   const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Any';
    const date = new Date(timestamp);
    return date.toDateString().substring(4) + ' ' + date.toLocaleTimeString();
  };

  const handleSort = (key) => {
      if (sortKey === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortKey(key);
        setSortDir('asc');
      }
      setPage(1);
    };  
  
  const sortIndicator = (key) => (sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');
    
  const filteredRoutingRestrictions = React.useMemo(() => {
      const term = searchTerm.trim().toLowerCase();
      if (!term) return restrictions;
      return restrictions.filter((restriction) => {
        return [
          restriction.restrictionName,
          restriction.restrictionId,
          restriction.instanceId,
          getRestrictionStatusLabel(restriction),
          formatCriteria(restriction.contactCriteria),
          ...(restriction.contactCriteria?.conditions || [])
            .flatMap((condition) => [
              condition.attributeId,
              condition.operator,
              condition.value
            ]),
          formatActions(restriction.actions),
          ...(restriction.routingSteps || []).flatMap((step) => [
            step.step,
            step.priority,
            step.routingGroupId,
            step.timeoutSeconds == null ? 'no timeout' : `${step.timeoutSeconds}s`,
          ])
        ].some((value) => String(value || '').toLowerCase().includes(term));
      });
    }, [restrictions, searchTerm]);

  const sortedRoutingRestrictions = React.useMemo(() => {
      if (!sortKey) return filteredRoutingRestrictions;
      const copy = [...filteredRoutingRestrictions];
      copy.sort((a, b) => {
        const va = getSortValue(a, sortKey);
        const vb = getSortValue(b, sortKey);
        if (va < vb) return sortDir === 'asc' ? -1 : 1;
        if (va > vb) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
      return copy;
    }, [filteredRoutingRestrictions, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedRoutingRestrictions.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedRoutingRestrictions = sortedRoutingRestrictions.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  }; 

  const handleDeleteRoutingRestriction = (restriction) => {
    setConfirmDialog({isOpen: true, restriction});
  };

  const confirmDeleteRoutingRestriction = async () => {
    const restriction = confirmDialog.restriction;
    setConfirmDialog({isOpen: false, restriction: null});
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const userString = localStorage.getItem('user');
      const user = userString ? JSON.parse(userString) : null;
      const response = await apiService.post(`${endpoints.deleteRoutingRestrictions}/${restriction.restrictionId}`, {
        instanceId: restriction.instanceId,
        restrictionId: restriction.restrictionId,
        user_role: user?.role || ''
      })
        
      if (response.data.status !== 'ok') {
        throw new Error(response.data.message || 'Failed to delete routing restriction');
      }
      await loadInstanceData(restriction.instanceId);
      setResult({ status: 'ok', message: `Routing restriction '${restriction.restrictionId}' deleted successfully.` });
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to delete routing restriction');
    } finally {
      setLoading(false);
    }
  };

  const cancelDeleteRoutingRestriction = () => {
    setConfirmDialog({ isOpen: false, restriction: null });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await apiService.post(
        endpoints.routingRestrictionRegistry,
        getPayload()
      );

      if (response.data.status !== 'ok') {
        throw new Error(response.data.message || 'Failed to create restriction');
      }

      await loadInstanceData(form.instanceId);
      setResult({ message: 'Routing restriction created successfully.' });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create restriction');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadInstances = async () => {
      setLoadingInstances(true);

      try {
        const response = await apiService.get(endpoints.instanceDropdown);
        if (response.data.status === 'ok') {
          setInstances(response.data.instances || []);
        }
      } catch (err) {
        setError(err.message || 'Failed to load instances');
      } finally {
        setLoadingInstances(false);
      }
    };

    loadInstances();
  }, [endpoints.instanceDropdown]);

  return (
    <div className="setup-page">
      <div className="page-header">
        <div>
          <h1>Routing Restrictions</h1>
          <p className="page-subtitle">
            Create and manage contact routing restrictions
          </p>
        </div>
        <Link to="/routing-admin" className="btn btn-secondary">
          ← Back to Routing Administration
        </Link>
      </div>

       {/* Environment Selector */}
      <div className="environment-selector" style={{
          backgroundColor: '#f9f9f9',
          border: '2px solid #e0e0e0',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <label htmlFor="environment" style={{ 
            fontWeight: 'bold', 
            fontSize: '16px',
            minWidth: '120px'
          }}>
            Environment:
          </label>
          <select
            id="environment"
            value={selectedEnvironment}
            onChange={handleEnvironmentChange}
            style={{
              padding: '10px 16px',
              fontSize: '15px',
              borderRadius: '6px',
              border: '2px solid #ccc',
              backgroundColor: 'white',
              cursor: 'pointer',
              fontWeight: '500',
              minWidth: '200px'
            }}
          >
            {availableEnvironments.map(env => (
              <option key={env} value={env}>
                {ENVIRONMENT_LABELS[env]} ({env})
              </option>
            ))}
          </select>
          <span style={{
            padding: '6px 12px',
            borderRadius: '4px',
            fontSize: '13px',
            fontWeight: 'bold',
            color: 'white',
            backgroundColor: getEnvironmentColor(selectedEnvironment)
          }}>
            {selectedEnvironment}
          </span>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit} className="setup-form">
          <div className="form-section">
            <h3>New Routing Restriction</h3>

            <div className="form-group">
              <label htmlFor="instanceId">Instance *</label>
              <select
                id="instanceId"
                value={form.instanceId}
                onChange={handleInstanceChange}
                required
                disabled={loadingInstances}
              >
                <option value="">
                  {loadingInstances ? 'Loading instances...' : 'Select an instance...'}
                </option>
                {instances.map((instance) => (
                  <option key={instance.id} value={instance.id}>
                    {instance.name} ({instance.status})
                  </option>
                ))}
              </select>
              <small>Select an Amazon Connect instance</small>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="restrictionId">Restriction ID *</label>
                <input
                  id="restrictionId"
                  value={form.restrictionId}
                  onChange={(e) => updateForm('restrictionId', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="restrictionName">Restriction Name *</label>
                <input
                  id="restrictionName"
                  value={form.restrictionName}
                  onChange={(e) => updateForm('restrictionName', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={form.description}
                onChange={(e) => updateForm('description', e.target.value)}
                rows="2"
              />
            </div>

            <fieldset className="allowed-values-fieldset">
              <legend>Contact Criteria</legend>
              <div className="form-group">
                <label htmlFor="criteriaLogic">Logic</label>
                <select
                  id="criteriaLogic"
                  value={form.contactCriteria.logic}
                  onChange={(e) => updateForm('contactCriteria', {
                    ...form.contactCriteria,
                    logic: e.target.value
                  })}
                >
                  <option value="AND">AND</option>
                  <option value="OR">OR</option>
                </select>
              </div>

              {form.contactCriteria.conditions.map((condition, index) => {
                const attribute = attributes.find(
                  (item) => item.attributeId === condition.attributeId
                );
                const operators = Object.keys(attribute?.allowedOperators || {})
                  .filter((operator) => attribute.allowedOperators[operator]);
                const availableOperators = operators.length > 0 ? operators : ['EQUALS'];

                return (
                  <div className="allowed-value-row" key={index}>
                    <div className="form-group">
                      <label htmlFor={`attribute-${index}`}>Attribute</label>
                      <select
                        id={`attribute-${index}`}
                        value={condition.attributeId}
                        onChange={(e) => updateCondition(index, 'attributeId', e.target.value)}
                        required
                        disabled={!form.instanceId || loadingData}
                      >
                        <option value="">Select an attribute</option>
                        {attributes
                          .filter((item) => item.active !== false)
                          .map((item) => (
                            <option key={item.attributeId} value={item.attributeId}>
                              {item.attributeId}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor={`operator-${index}`}>Operator</label>
                      <select
                        id={`operator-${index}`}
                        value={condition.operator}
                        onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                        required
                      >
                        <option value="">Select an operator</option>
                        {availableOperators.map((operator) => (
                          <option key={operator} value={operator}>
                            {operator}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor={`value-${index}`}>Value</label>
                      <select
                        id={`value-${index}`}
                        value={condition.value}
                        onChange={(e) => updateCondition(index, 'value', e.target.value)}
                        required
                        disabled={!attribute || !condition.operator}
                      >
                        <option value="">Select a value</option>
                        {(attribute?.allowedValues || []).map((value) => (
                          <option key={value.value} value={value.value}>
                            {value.value} ({value.displayName})
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      className="btn btn-secondary allowed-value-remove"
                      onClick={() => removeCondition(index)}
                      disabled={form.contactCriteria.conditions.length === 1}
                    >
                      Remove
                    </button>
                  </div>
                );
              })}
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={addCondition}>
                  Add Condition
                </button>
              </div>
            </fieldset>

            <fieldset className="allowed-values-fieldset">
              <legend>Actions</legend>
              {form.actions.map((action, index) => (
                <div className="criteria-value-row" key={index}>
                  <div className="form-group">
                    <label htmlFor={`action-type-${index}`}>Action Type</label>
                    <select
                      id={`action-type-${index}`}
                      value={action.type}
                      onChange={(e) => handleActionTypeChange(index, e.target.value)}
                      required
                    >
                      {ACTION_TYPES.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  {isNumericAction(action.type) ? (
                    <div className="form-group">
                      <label htmlFor={`action-value-${index}`}>
                        {action.type === ROUTING_ACTION_TYPES.CHANGE_TIMEOUT
                          ? 'Timeout (seconds)'
                          : 'Priority'}
                      </label>
                      <select
                        id={`action-value-${index}`}
                        value={action.type === ROUTING_ACTION_TYPES.CHANGE_TIMEOUT
                          ? action.timeoutSeconds
                          : action.priority}
                        onChange={(e) => updateAction(
                          index,
                          action.type === ROUTING_ACTION_TYPES.CHANGE_TIMEOUT
                            ? 'timeoutSeconds'
                            : 'priority',
                          e.target.value
                        )}
                        required
                      >
                        <option value="">Select a number</option>
                        {(action.type === ROUTING_ACTION_TYPES.CHANGE_TIMEOUT
                          ? TIMEOUT_OPTIONS
                          : PRIORITY_OPTIONS
                        ).map((value) => (
                          <option key={value} value={value}>{value}</option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="form-group">
                      <label htmlFor={`action-groups-${index}`}>Routing Groups</label>
                      <select
                        id={`action-groups-${index}`}
                        multiple
                        value={action.routingGroupIds}
                        onChange={(e) => updateRoutingGroups(index, e)}
                        required
                        disabled={!form.instanceId || loadingData}
                      >
                        {groups
                          .filter((group) => group.active !== false)
                          .map((group) => (
                            <option key={group.routingGroupId} value={group.routingGroupId}>
                              {group.routingGroupId} ({group.displayName})
                            </option>
                          ))}
                      </select>
                    </div>
                  )}
                  <button
                    type="button"
                    className="btn btn-secondary allowed-value-remove"
                    onClick={() => removeAction(index)}
                    disabled={form.actions.length === 1}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button type="button" className="btn btn-secondary" onClick={addAction}>
                Add Action
              </button>
            </fieldset>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="startTime">Start Time</label>
                <input
                  id="startTime"
                  type="datetime-local"
                  value={form.effectiveTime.startTime}
                  onChange={(e) => updateForm('effectiveTime', {
                    ...form.effectiveTime,
                    startTime: e.target.value
                  })}
                />
              </div>
              <div className="form-group">
                <label htmlFor="endTime">End Time</label>
                <input
                  id="endTime"
                  type="datetime-local"
                  value={form.effectiveTime.endTime}
                  onChange={(e) => updateForm('effectiveTime', {
                    ...form.effectiveTime,
                    endTime: e.target.value
                  })}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="timezone">Timezone</label>
              <select
                id="timezone"
                value={form.effectiveTime.timezone}
                onChange={(e) => updateForm('effectiveTime', {
                  ...form.effectiveTime,
                  timezone: e.target.value
                })}
              >
                {US_TIME_ZONES.map((timeZone) => (
                  <option key={timeZone.value} value={timeZone.value}>
                    {timeZone.label} ({timeZone.value})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group attribute-active-control">
              <label htmlFor="status">
                <input
                  id="status"
                  type="checkbox"
                  checked={form.status === 'Active'}
                  onChange={(e) => updateForm(
                    'status',
                    e.target.checked ? 'Active' : 'Inactive'
                  )}
                />
                Active
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowPreview(true)}
            >
              Preview JSON
            </button>
            <button type="button" className="btn btn-secondary" onClick={validateRestriction}>
              Validate Restriction
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || loadingData}
            >
              {loading ? 'Creating...' : 'Create Restriction'}
            </button>
          </div>
        </form>
      </div>

      {showPreview && (
        <div
          className="json-preview-overlay"
          role="presentation"
          onClick={() => setShowPreview(false)}
        >
          <div
            className="json-preview-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="restriction-preview-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="json-preview-header">
              <h3 id="restriction-preview-title">Restriction JSON Preview</h3>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowPreview(false)}
              >
                Close
              </button>
            </div>
            <pre className="json-preview-content">
              {JSON.stringify(getPayload(), null, 2)}
            </pre>
          </div>
        </div>
      )}

      {restrictions.length > 0 && (
        <div className="table-format-list">
          <div className="table-format-list-header">
            <h3>Existing Restrictions ({restrictions.length})</h3>
            <div className="table-format-list-controls">
              <input
                type="text"
                className="table-format-search-input"
                placeholder="Search Routing Policies..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              />
              <div className="page-size-selector">
                <label htmlFor="page_size">Rows per page:</label>
                <select id="page_size" value={pageSize} onChange={handlePageSizeChange}>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th className="sortable" onClick={() => handleSort('restrictionId')}>Restriction ID{sortIndicator('restrictionId')}</th>
                <th className="sortable" onClick={() => handleSort('restrictionName')}>Name{sortIndicator('restrictionName')}</th>
                <th className="sortable" onClick={() => handleSort('contactCriteria')}>Criteria{sortIndicator('contactCriteria')}</th>
                <th className="sortable" onClick={() => handleSort('actions')}>Actions{sortIndicator('actions')}</th>
                <th className="sortable" onClick={() => handleSort('effectiveTime')}>Effective Time{sortIndicator('effectiveTime')}</th>
                <th className="sortable" onClick={() => handleSort('status')}>Status{sortIndicator('status')}</th>
                <th>Manage Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedRoutingRestrictions.map((restriction) => (
                <tr key={restriction.restrictionId}>
                  <td>{restriction.restrictionId}</td>
                  <td>{restriction.restrictionName}</td>
                  <td>
                    {formatCriteria(restriction.contactCriteria)}
                  </td>
                  <td>
                    {formatActions(restriction.actions)}
                  </td>
                  <td>
                    {formatTimestamp(restriction.effectiveTime?.startTime) || 'Any'} to{' '}
                    {formatTimestamp(restriction.effectiveTime?.endTime) || 'Any'}
                  </td>
                  <td>{restriction.status}</td>
                  <td>
                    <button type="button" className="btn btn-secondary" onClick={() => loadRestriction(restriction)}>
                      Load
                    </button>
                    <button type="button" className="btn btn-danger" onClick={() => handleDeleteRoutingRestriction(restriction)} style={{marginLeft: '4px'}}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pagination-controls">
              <button type="button" className="btn btn-secondary" onClick={() => setPage(1)} disabled={currentPage === 1}>« First</button>
              <button type="button" className="btn btn-secondary" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>‹ Prev</button>
              <span className="pagination-status">Page {currentPage} of {totalPages}</span>
              <button type="button" className="btn btn-secondary" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next ›</button>
              <button type="button" className="btn btn-secondary" onClick={() => setPage(totalPages)} disabled={currentPage === totalPages}>Last »</button>
            </div>
        </div>
      )}

      {!loadingData && form.instanceId && restrictions.length === 0 && (
        <div className="attribute-list">
          <h3>Existing Restrictions (0)</h3>
          <p>No routing restrictions found.</p>
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <h4>Error</h4>
          <p>{error}</p>
        </div>
      )}
      {result && (
        <div className="alert alert-success">
          <h4>Result</h4>
          <p>{result.message}</p>
        </div>
      )}

      <ConfirmationDialog
              isOpen={confirmDialog.isOpen}
              title="Delete Routing Restriction"
              message={`Are you sure you want to delete routing restriction '${confirmDialog.restriction?.restrictionId}'? This action cannot be undone.`}
              confirmText="Delete"
              cancelText="Cancel"
              isDanger={true}
              onConfirm={confirmDeleteRoutingRestriction}
              onCancel={cancelDeleteRoutingRestriction}
            />
            
    </div>
  );
};

export default RoutingRestrictionsPage;